import ImportanceIcon from './ImportanceIcon';
import { observer } from 'mobx-react';
import { CommandBarButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ITextField, TextField } from 'office-ui-fabric-react/lib/TextField';
import { ControlIcons } from 'owa-control-icons';
import { keydownHandler } from 'owa-hotkeys';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import showHideSubjectPicker from 'owa-mail-compose-store/lib/actions/showHideSubjectPicker';
import updateLastFocusedControl from 'owa-mail-compose-store/lib/actions/updateLastFocusedControl';
import updateSubject from 'owa-mail-compose-store/lib/actions/updateSubject';
import updateSubjectSelectionRange from 'owa-mail-compose-store/lib/actions/updateSubjectSelectionRange';
import ComposeViewState from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import { ComposeControl } from 'owa-mail-compose-store/lib/utils/getAutoFocusControl';
import getRecipientsCount from 'owa-mail-compose-store/lib/utils/getRecipientsCount';
import { isSubjectPickerSupported } from 'owa-mail-subject-picker-connector';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import {
    SubjectPicker,
    doesSubjectPickerSupportPersona,
    lazyBackButtonPressed,
    lazyBackButtonHovered,
} from 'owa-mail-subject-picker';
import { DarkModeContext } from 'owa-theme';

const styles = require('./ComposeTop.scss');
const classNames = require('classnames/bind').bind(styles);
const MAX_SUBJECT_LENGTH = 255;

export interface SubjectLineProps {
    composeViewState: ComposeViewState;
}

@observer
export default class SubjectLine extends React.Component<SubjectLineProps, {}> {
    private subjectInput: ITextField;
    private subjectLineElement: HTMLElement;
    private subjectPickerElement: any;
    static contextType = DarkModeContext;

    constructor(props: SubjectLineProps) {
        super(props);
    }

    render() {
        const {
            subject,
            importance,
            subjectViewState,
            smimeViewState,
        } = this.props.composeViewState;
        const isDarkMode = this.context;
        const isSubjectPickerEnabled = this.isSubjectPickerEnabled();
        const showBackButton =
            isSubjectPickerSupported() &&
            this.props.composeViewState.subjectPickerViewState.showBackButton;

        return (
            <div className={styles.subjectContainer}>
                <div className={styles.subjectIconContainer}>
                    <ImportanceIcon importance={importance} />
                </div>
                <div className={styles.subjectLine} ref={this.onSubjectLineElementRendered}>
                    {showBackButton && (
                        <div className={styles.backButtonContainer}>
                            <CommandBarButton
                                iconProps={{ iconName: ControlIcons.ChevronLeft }}
                                className={styles.backButton}
                                onClick={async () => {
                                    const backButtonPressed = await lazyBackButtonPressed.import();
                                    backButtonPressed(this.props.composeViewState);
                                }}
                                onMouseOver={async () => {
                                    const backButtonHovered = await lazyBackButtonHovered.import();
                                    backButtonHovered(true, this.props.composeViewState);
                                }}
                                onMouseOut={async () => {
                                    const backButtonHovered = await lazyBackButtonHovered.import();
                                    backButtonHovered(false, this.props.composeViewState);
                                }}
                            />
                        </div>
                    )}
                    <TextField
                        id={subjectViewState.inputId}
                        componentRef={(ref: ITextField) => (this.subjectInput = ref)}
                        className={classNames(
                            styles.subject,
                            this.props.composeViewState.subjectPickerViewState
                                .isBackButtonHovered && styles.fadedOut
                        )}
                        value={subject}
                        inputClassName={isDarkMode && styles.darkModeInput}
                        maxLength={MAX_SUBJECT_LENGTH}
                        borderless={true}
                        autoComplete={'off'}
                        type={'text'}
                        placeholder={strings.addSubjectPlaceholder}
                        onChanged={this.onSubjectChange}
                        onSelect={this.onSelect}
                        onFocus={this.onSubjectInputFocus}
                        onKeyDown={isSubjectPickerEnabled ? this.onKeyDown : undefined}
                    />
                    {smimeViewState && smimeViewState.shouldSignMessageAsSmime && (
                        <Icon iconName={ControlIcons.Ribbon} className={styles.ribbonIcon} />
                    )}
                    {smimeViewState && smimeViewState.shouldEncryptMessageAsSmime && (
                        <Icon iconName={ControlIcons.Lock} className={styles.lockIcon} />
                    )}
                </div>

                {isSubjectPickerEnabled && (
                    <div>
                        <SubjectPicker
                            ref={ref => (this.subjectPickerElement = ref)}
                            filter={subject}
                            targetElement={this.subjectLineElement}
                            isOpened={
                                this.props.composeViewState.subjectPickerViewState.isPickerOpened
                            }
                            composeViewState={this.props.composeViewState}
                            onDismiss={this.onSubjectPickerDismiss}
                            onConversationSelected={this.onSubjectConversationSelected}
                        />
                    </div>
                )}
            </div>
        );
    }

    componentDidUpdate() {
        this.updateSelectionRange();
    }

    updateSelectionRange() {
        const { subjectViewState } = this.props.composeViewState;
        const { selectionStart, selectionEnd } = this.subjectInput;
        if (
            selectionStart != subjectViewState.selectionStart ||
            selectionEnd != subjectViewState.selectionEnd
        ) {
            updateSubjectSelectionRange(this.props.composeViewState, selectionStart, selectionEnd);
        }
    }

    public focus = () => {
        this.subjectInput.focus();
    };

    private onSubjectLineElementRendered = (ref: HTMLElement) => {
        this.subjectLineElement = ref;

        if (this.props.composeViewState.subjectPickerViewState.isPickerOpened) {
            // We need to re-render in case we get back to tab containing subject picker
            this.forceUpdate();
        }
    };

    private onSelect = () => {
        this.updateSelectionRange();
    };

    private onSubjectChange = () => {
        updateSubject(this.props.composeViewState, this.subjectInput.value);

        if (this.isSubjectPickerEnabled() && this.shouldOpenSubjectPicker()) {
            showHideSubjectPicker(
                this.props.composeViewState.subjectPickerViewState,
                true /* isOpened */,
                'SubjectCleared'
            );
        }
    };

    private onSubjectInputFocus = () => {
        if (this.isSubjectPickerEnabled() && this.shouldOpenSubjectPicker()) {
            showHideSubjectPicker(
                this.props.composeViewState.subjectPickerViewState,
                true /* isOpened */,
                'InputFocused'
            );
        }

        updateLastFocusedControl(this.props.composeViewState, ComposeControl.SubjectLine);
    };

    private onSubjectPickerDismiss = (isDismissedOnCloseButton: boolean) => {
        showHideSubjectPicker(
            this.props.composeViewState.subjectPickerViewState,
            false /* isOpened */,
            isDismissedOnCloseButton ? 'PopupClosedByXButton' : 'PopupDismissed'
        );
    };

    private onSubjectConversationSelected = (conversation: ConversationType) => {
        showHideSubjectPicker(
            this.props.composeViewState.subjectPickerViewState,
            false /* isOpened */,
            'ConversationSelected'
        );
        updateSubject(this.props.composeViewState, conversation.ConversationTopic);
    };

    private onKeyDown = (target: React.KeyboardEvent<EventTarget>) => {
        if (target.keyCode === KeyboardCharCodes.Down_arrow && this.subjectPickerElement) {
            const innerComponent = this.subjectPickerElement.getInnerComponent();

            if (innerComponent) {
                target.preventDefault();
                innerComponent.focus();
            }
        }
        // Escape is handled in @keydownHandler since it overrides a keybinding from Compose.tsx
    };

    private isSubjectPickerEnabled(): boolean {
        return (
            isSubjectPickerSupported() &&
            this.props.composeViewState.subjectPickerViewState.supportsSubjectPicker
        );
    }

    private shouldOpenSubjectPicker(): boolean {
        return (
            !this.props.composeViewState.subjectPickerViewState.isPickerOpened &&
            this.props.composeViewState.subject === '' &&
            this.props.composeViewState.initialContent === this.props.composeViewState.content &&
            this.isOnlyOneSupportedToRecipient()
        );
    }

    private isOnlyOneSupportedToRecipient(): boolean {
        const isOnlyOneToRecipient =
            getRecipientsCount(this.props.composeViewState.toRecipientWell) === 1 &&
            getRecipientsCount(this.props.composeViewState.ccRecipientWell) === 0 &&
            getRecipientsCount(this.props.composeViewState.bccRecipientWell) === 0;

        if (isOnlyOneToRecipient) {
            const toRecipientPersona = this.props.composeViewState.toRecipientWell.recipients[0]
                .persona;
            return doesSubjectPickerSupportPersona(toRecipientPersona);
        }

        return false;
    }

    @keydownHandler(
        'ctrl+z',
        true /*stopPropagation*/,
        false /*preventDefault*/,
        true /*allowHotkeyOnTextFields*/
    )
    stopUndoPropagation() {
        // do nothing -- this keydown handler stops propagation so that the mailmodule
        // does not eat the undo action & the browser's native Undo functionality can work.
    }

    @keydownHandler(
        'escape',
        false /*stopPropagation*/,
        false /*preventDefault*/,
        true /*allowHotkeyOnTextFields*/
    )
    closeSubjectPicker(evt: KeyboardEvent) {
        if (
            this.isSubjectPickerEnabled() &&
            this.props.composeViewState.subjectPickerViewState.isPickerOpened
        ) {
            showHideSubjectPicker(
                this.props.composeViewState.subjectPickerViewState,
                false /* isOpened */,
                'EscPressed'
            );

            evt.stopPropagation();
        }
    }
}
